import { FC, useCallback, useMemo } from "react";

import classNames from "classnames";

import { AccessibilityProps } from "../../../../AccessibilityProps";
import { BaseProps } from "../../../../BaseProps";
import { Button, Buttons } from "../../../Button";
import { DataId } from "../../../DataId";
import { ActionProps } from "../Action";

/**
 * The `ActionButton` component properties.
 */
export interface ActionButtonProps extends ActionProps, BaseProps, Pick<AccessibilityProps, "aria-label"> {
  /**
   * The row ID.
   */
  readonly id: DataId;
}

/**
 * A single action button.
 */
export const ActionButton: FC<ActionButtonProps> = (props) => {
  const { id, icon, color = "turquoise", disabled = false, tooltip, onClick, testId, "aria-label": ariaLabel, type } = props;

  const isDisabled = useMemo(() => {
    return typeof disabled === "boolean" ? disabled : (disabled?.(id) ?? false);
  }, [disabled, id]);

  const clickHandler = useCallback(() => {
    onClick?.(id);
  }, [id, onClick]);

  let finalColor;
  if (type === "destructive") {
    finalColor = "red";
  } else {
    finalColor = color;
  }

  return (
    <Button
      className={classNames("action", finalColor)}
      icon={typeof icon === "function" ? icon(id) : icon}
      appearance={type === "destructive" ? Buttons.Destructive.Tertiary.Small : Buttons.Functional.Tertiary.Small}
      disabled={isDisabled}
      onClick={clickHandler}
      tooltip={typeof tooltip === "function" ? tooltip(id) : tooltip}
      testId={testId}
      aria-label={ariaLabel ?? "action button"}
    />
  );
};
